import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { toast, Toaster } from 'sonner';

import { UpdateBanner } from '@/components/shared/UpdateBanner';
import { useAppVersion } from '@/compositions/hooks/useAppVersion';
import { router } from '@/routes';
import { getErrorMessage } from '@/utils/helpers/errors';

/**
 * Shell da aplicação.
 *
 * O banner de atualização é montado aqui, uma única vez e fora do roteador:
 * ele precisa sobreviver à troca de rota (docs/07-mobile-e-pwa.md §11).
 *
 * O cliente é criado **fora** do componente: instanciado dentro, cada render
 * faria um novo e levaria o cache junto. O sintoma seria a busca refazendo a
 * requisição a cada tecla, apesar do `staleTime` configurado.
 *
 * Os defaults miram rede móvel instável, que é o cenário real do produto:
 * `retry: 1` porque insistir numa rede caída só atrasa a mensagem de erro, e
 * `refetchOnWindowFocus: false` porque em telefone o app perde e ganha foco o
 * tempo todo (notificação, troca de app) e refazer tudo a cada volta gasta
 * dados de quem pode estar sem.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
    mutations: {
      // Escrita não se repete: repetir uma criação de conta produziria duas.
      retry: 0,
      // O **único** toast de erro de mutação do app. Declarar `onError` numa
      // mutation específica só para dar toast mostraria a mensagem duas vezes —
      // ver docs/04-estado-e-dados.md. As mutations declaram `onError` apenas
      // quando há efeito extra (rollback, log de contexto).
      onError: (error) => toast.error(getErrorMessage(error)),
    },
  },
});

/** Só o rodapé é nosso; laterais seguem o default do sonner. */
const TOAST_OFFSET = { bottom: 'var(--toast-offset-bottom)' };

export default function App() {
  const { updateAvailable, applyUpdate, dismissUpdate } = useAppVersion();

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {updateAvailable && <UpdateBanner onUpdate={applyUpdate} onDismiss={dismissUpdate} />}
      {/*
        Rodapé, acima da barra (decisão de produto, passada de UX 2026-10). No
        topo ele cobria o `PageHeader` inteiro — o voltar e o perfil — por ~4 s
        depois de cada mutação. O medo antigo (toast por cima do SOS) é o que o
        offset resolve: `--toast-offset-bottom` assenta acima da barra e do domo
        do SOS quando há barra, e só acima da safe area quando não há
        (`src/index.css`, docs/07 §4, "Toast").

        `mobileOffset` também: abaixo de 600px o sonner troca `offset` por ele,
        com 16px de default — sem isso o toast voltaria a cair sobre a barra
        justamente no telefone.

        `richColors` deixa erro e sucesso distinguíveis sem depender só do texto.
      */}
      <Toaster
        position="bottom-center"
        offset={TOAST_OFFSET}
        mobileOffset={TOAST_OFFSET}
        richColors
        closeButton
      />
    </QueryClientProvider>
  );
}
